import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import http from '../api/http';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';
import {
  Plus, Edit, Trash2, RefreshCw, X, Search,
  Layers, Coins, TrendingUp, Award,
  ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, AlertTriangle
} from 'lucide-react';

/* ---------- Helpers ---------- */
const toNum = (v) => parseInt(v, 10) || 0;
const fmt = (n) => (n ? parseInt(n, 10).toLocaleString('fr-FR') : '—');
const initials = (label = '') =>
  label.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';

// La devise vient directement de la table filiale (colonne `devise`, renvoyée par l'API)
const getGrilleDevise = (g) => (g && g.devise) || 'FCFA';
const totalOf = (g) => toNum(g.salaire_base) + toNum(g.sursalaire);

const getPageNumbers = (total, current) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current - 1, current, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push('…');
    out.push(p);
  });
  return out;
};

const inputClass =
  'w-full rounded-xl border border-gray-300 bg-white px-3.5 py-2.5 text-sm text-gray-900 outline-none transition ' +
  'placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20';

const GrillesPersonnel = () => {
  const navigate = useNavigate();
  const { filialeId, isConsolidated, currentFiliale } = useFiliale();
  const [grilles, setGrilles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sort, setSort] = useState({ key: null, dir: 'asc' });
  const [formData, setFormData] = useState({
    filiale_id: '',
    Libelle_categorie: '',
    salaire_base: '',
    sursalaire: ''
  });

  const fetchGrilles = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (!isConsolidated && filialeId && filialeId !== 'all') {
        params.filiale_id = filialeId;
      }
      const response = await http.get('/api/grille-personnel', { params });
      setGrilles(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur chargement grilles:', error);
      setGrilles([]);
    } finally {
      setLoading(false);
    }
  }, [filialeId, isConsolidated]);

  useEffect(() => {
    fetchGrilles();
  }, [fetchGrilles]);

  const handleOpenModal = (grille = null) => {
    if (grille) {
      setEditData(grille);
      setFormData({
        filiale_id: grille.filiale_id || filialeId || '',
        Libelle_categorie: grille.Libelle_categorie || '',
        salaire_base: grille.salaire_base || '',
        sursalaire: grille.sursalaire || ''
      });
    } else {
      setEditData(null);
      setFormData({
        filiale_id: filialeId || '',
        Libelle_categorie: '',
        salaire_base: '',
        sursalaire: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setEditData(null);
    setFormData({
      filiale_id: filialeId || '',
      Libelle_categorie: '',
      salaire_base: '',
      sursalaire: ''
    });
  }, [filialeId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editData) {
        await http.put(`/api/grille-personnel/${editData.idgrille_personnel}`, formData);
      } else {
        await http.post('/api/grille-personnel', formData);
      }
      setShowModal(false);
      fetchGrilles();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    try {
      await http.delete(`/api/grille-personnel/${id}`);
      setDeleteTarget(null);
      fetchGrilles();
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  // Fermeture des modales avec Échap
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setDeleteTarget(null);
        if (showModal) handleCloseModal();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showModal, handleCloseModal]);

  // Filtrage par recherche
  const filteredGrilles = useMemo(
    () =>
      grilles.filter(
        (grille) =>
          (grille.Libelle_categorie || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (grille.salaire_base && grille.salaire_base.toString().includes(searchTerm)) ||
          (grille.sursalaire && grille.sursalaire.toString().includes(searchTerm)) ||
          (grille.filiale_libelle && grille.filiale_libelle.toLowerCase().includes(searchTerm.toLowerCase()))
      ),
    [grilles, searchTerm]
  );

  // Tri
  const sortedGrilles = useMemo(() => {
    if (!sort.key) return filteredGrilles;
    const val = (g) =>
      sort.key === 'total' ? totalOf(g)
      : sort.key === 'Libelle_categorie' ? (g.Libelle_categorie || '').toLowerCase()
      : toNum(g[sort.key]);
    return [...filteredGrilles].sort((a, b) => {
      const x = val(a), y = val(b);
      return (x > y ? 1 : x < y ? -1 : 0) * (sort.dir === 'asc' ? 1 : -1);
    });
  }, [filteredGrilles, sort]);

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentGrilles = sortedGrilles.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedGrilles.length / itemsPerPage);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const handlePageChange = (page) => setCurrentPage(page);

  // Devises présentes : en consolidé on peut mélanger XOF, XAF, GNF…
  const devises = useMemo(() => [...new Set(grilles.map(getGrilleDevise))], [grilles]);
  const multiDevises = devises.length > 1;
  const uniqueDevise = devises[0] || 'FCFA';

  // Indicateurs
  const stats = useMemo(() => {
    const n = grilles.length;
    const avg = (k) => (n ? grilles.reduce((s, g) => s + toNum(g[k]), 0) / n : 0);
    const top = grilles.reduce((m, g) => (!m || totalOf(g) > totalOf(m) ? g : m), null);
    return { n, base: avg('salaire_base'), sur: avg('sursalaire'), top };
  }, [grilles]);

  // Les barres sont comparées au sein d'une même devise (un GNF n'est pas un XOF)
  const maxByDevise = useMemo(() => {
    const m = {};
    currentGrilles.forEach((g) => {
      const d = getGrilleDevise(g);
      m[d] = Math.max(m[d] || 1, totalOf(g));
    });
    return m;
  }, [currentGrilles]);

  const formTotal = toNum(formData.salaire_base) + toNum(formData.sursalaire);

  // Devise affichée dans la modale
  const modalDevise =
    (editData && editData.devise) ||
    (currentFiliale && currentFiliale.devise) ||
    (grilles.find((g) => String(g.filiale_id) === String(formData.filiale_id)) || {}).devise ||
    'FCFA';

  const SortHead = ({ k, children, className = '' }) => (
    <th className={`px-6 py-3.5 text-left ${className}`}>
      <button
        type="button"
        onClick={() => toggleSort(k)}
        className={`inline-flex items-center gap-1.5 text-[13px] font-semibold transition-colors hover:text-blue-600 ${
          sort.key === k ? 'text-blue-600' : 'text-gray-500'
        }`}
      >
        {children}
        {sort.key === k ? (
          sort.dir === 'asc' ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />
        )}
      </button>
    </th>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <BlueHeader
        title="Grilles des Salaires"
        subtitle="Gestion des grilles de rémunération par filiale"
        actions={[
          {
            label: 'Nouvelle grille',
            icon: Plus,
            onClick: () => handleOpenModal()
          },
          {
            label: 'Actualiser',
            icon: RefreshCw,
            onClick: fetchGrilles
          }
        ]}
        showBack={true}
        onBack={() => navigate('/dashboard')}
      />

      <div className="space-y-6 px-4 py-8 sm:px-6">
        {/* ---------- Indicateurs ---------- */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[
            {
              label: 'Grilles actives',
              value: stats.n,
              hint: isConsolidated ? 'Toutes filiales confondues' : 'Filiale courante',
              icon: Layers
            },
            {
              label: 'Salaire de base moyen',
              value: multiDevises ? 'Multi-devises' : `${fmt(Math.round(stats.base))} ${uniqueDevise}`,
              hint: multiDevises ? `${devises.join(' · ')} : choisissez une filiale` : 'Moyenne des catégories',
              icon: Coins
            },
            {
              label: 'Sursalaire moyen',
              value: multiDevises ? 'Multi-devises' : `${fmt(Math.round(stats.sur))} ${uniqueDevise}`,
              hint: multiDevises ? `${devises.join(' · ')} : choisissez une filiale` : 'Moyenne des catégories',
              icon: TrendingUp
            }
          ].map(({ label, value, hint, icon: Icon }) => (
            <div key={label} className="flex items-center gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                <Icon className="h-6 w-6" />
              </div>
              <div className="min-w-0">
                <p className="text-sm text-gray-500">{label}</p>
                <p className="truncate text-2xl font-bold tracking-tight text-gray-900">{loading ? '…' : value}</p>
                <p className="truncate text-xs text-gray-400">{hint}</p>
              </div>
            </div>
          ))}
          <div className="flex items-center gap-4 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 p-5 text-white shadow-lg shadow-blue-600/20">
            <div className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-white/15">
              <Award className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-blue-100">Rémunération la plus haute</p>
              <p className="truncate text-2xl font-bold tracking-tight">
                {loading || !stats.top
                  ? '…'
                  : multiDevises
                  ? 'Multi-devises'
                  : `${fmt(totalOf(stats.top))} ${getGrilleDevise(stats.top)}`}
              </p>
              <p className="truncate text-xs text-blue-100">
                {stats.top ? (multiDevises ? 'Voir le détail par filiale' : stats.top.Libelle_categorie) : ''}
              </p>
            </div>
          </div>
        </div>

        {/* ---------- Tableau ---------- */}
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          {/* Barre de recherche */}
          <div className="flex flex-wrap items-center gap-3 border-b border-gray-100 p-4">
            <div className="relative min-w-[240px] flex-1">
              <Search className="absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par catégorie, salaire de base ou sursalaire..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className={`${inputClass} pl-11 pr-10`}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => { setSearchTerm(''); setCurrentPage(1); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  aria-label="Effacer la recherche"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {!loading && (
              <span className="rounded-full bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-600">
                {sortedGrilles.length} grille{sortedGrilles.length > 1 ? 's' : ''}
              </span>
            )}
          </div>

          {loading ? (
            <div className="divide-y divide-gray-100" aria-busy="true">
              <div className="flex items-center gap-2 px-6 py-3 text-sm text-gray-400">
                <RefreshCw className="h-4 w-4 animate-spin text-blue-600" />
                Chargement...
              </div>
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex animate-pulse items-center gap-6 px-6 py-4">
                  <div className="h-6 w-28 rounded-full bg-gray-100" />
                  <div className="h-10 w-10 rounded-xl bg-gray-100" />
                  <div className="h-4 w-40 rounded bg-gray-100" />
                  <div className="ml-auto h-4 w-24 rounded bg-gray-100" />
                </div>
              ))}
            </div>
          ) : sortedGrilles.length === 0 ? (
            <div className="flex flex-col items-center gap-3 px-4 py-16 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                <Search className="h-6 w-6" />
              </div>
              <p className="text-sm font-medium text-gray-900">Aucune grille trouvée pour votre recherche.</p>
              <p className="text-sm text-gray-500">Modifiez votre recherche ou créez une nouvelle grille.</p>
              <button
                onClick={() => handleOpenModal()}
                className="mt-1 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
              >
                <Plus className="h-4 w-4" /> Nouvelle grille
              </button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3.5 text-left text-[13px] font-semibold text-gray-500">Filiale</th>
                      <SortHead k="Libelle_categorie">Catégorie</SortHead>
                      <SortHead k="salaire_base">Salaire de base</SortHead>
                      <SortHead k="sursalaire">Sursalaire</SortHead>
                      <SortHead k="total">Rémunération totale</SortHead>
                      <th className="px-6 py-3.5 text-right text-[13px] font-semibold text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {currentGrilles.map((grille, index) => {
                      const base = toNum(grille.salaire_base);
                      const sur = toNum(grille.sursalaire);
                      const devise = getGrilleDevise(grille);
                      const max = maxByDevise[devise] || 1;
                      return (
                        <tr key={`grille-${grille.idgrille_personnel || index}`} className="group transition-colors hover:bg-blue-50/40">
                          <td className="px-6 py-4">
                            <span className="inline-flex rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 ring-1 ring-inset ring-blue-100">
                              {grille.filiale_libelle || `Filiale ${grille.filiale_id}`}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-blue-400 text-xs font-bold text-white shadow-sm">
                                {initials(grille.Libelle_categorie)}
                              </div>
                              <span className="text-sm font-semibold text-gray-900">{grille.Libelle_categorie}</span>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm tabular-nums text-gray-700">
                            {fmt(grille.salaire_base)} <span className="text-xs text-gray-400">{devise}</span>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm tabular-nums text-gray-700">
                            {sur ? (
                              <span className="text-blue-600">
                                + {fmt(sur)} <span className="text-xs text-blue-400">{devise}</span>
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="whitespace-nowrap text-sm font-bold tabular-nums text-gray-900">
                              {fmt(base + sur)} <span className="text-xs font-medium text-gray-400">{devise}</span>
                            </div>
                            <div
                              className="mt-1.5 flex h-1.5 w-40 overflow-hidden rounded-full bg-gray-100"
                              title="Salaire de base + sursalaire"
                            >
                              <div className="h-full bg-blue-600" style={{ width: `${(base / max) * 100}%` }} />
                              <div className="h-full bg-blue-300" style={{ width: `${(sur / max) * 100}%` }} />
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                key={`edit-${grille.idgrille_personnel}`}
                                onClick={() => handleOpenModal(grille)}
                                className="rounded-xl p-2 text-blue-600 transition-colors hover:bg-blue-100"
                                title="Modifier"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                key={`delete-${grille.idgrille_personnel}`}
                                onClick={() => setDeleteTarget(grille)}
                                className="rounded-xl p-2 text-red-600 transition-colors hover:bg-red-50"
                                title="Supprimer"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Légende */}
              <div className="flex flex-wrap items-center gap-5 border-t border-gray-100 px-6 py-3 text-xs text-gray-500">
                <span className="inline-flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-sm bg-blue-600" /> Salaire de base</span>
                <span className="inline-flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-sm bg-blue-300" /> Sursalaire</span>
                {multiDevises && (
                  <span className="text-gray-400">Barres comparées au sein d’une même devise ({devises.join(', ')})</span>
                )}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-6 py-4">
                  <div className="text-sm text-gray-500">
                    Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, sortedGrilles.length)} sur {sortedGrilles.length} grilles
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <ChevronLeft className="h-4 w-4" /> Précédent
                    </button>
                    {getPageNumbers(totalPages, currentPage).map((page, i) =>
                      page === '…' ? (
                        <span key={`gap-${i}`} className="px-1 text-gray-400">…</span>
                      ) : (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`h-9 min-w-[2.25rem] rounded-lg border px-2 text-sm font-medium transition ${
                            currentPage === page
                              ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      )
                    )}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Suivant <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ---------- Modal Ajout/Modification ---------- */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4 backdrop-blur-sm"
          onMouseDown={(e) => e.target === e.currentTarget && handleCloseModal()}
        >
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                  {editData ? <Edit className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {editData ? 'Modifier la grille' : 'Nouvelle grille'}
                  </h3>
                  <p className="text-sm text-gray-500">Renseignez la catégorie et sa rémunération.</p>
                </div>
              </div>
              <button
                onClick={handleCloseModal}
                className="rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
                aria-label="Fermer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">Catégorie</label>
                <input
                  type="text"
                  name="Libelle_categorie"
                  value={formData.Libelle_categorie}
                  onChange={(e) => setFormData({ ...formData, Libelle_categorie: e.target.value })}
                  className={inputClass}
                  placeholder="Ex. Cadre supérieur"
                  autoFocus
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Salaire de base</label>
                  <div className="relative">
                    <input
                      type="number"
                      name="salaire_base"
                      value={formData.salaire_base}
                      onChange={(e) => setFormData({ ...formData, salaire_base: e.target.value })}
                      className={`${inputClass} pr-14`}
                      required
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-400">{modalDevise}</span>
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Sursalaire</label>
                  <div className="relative">
                    <input
                      type="number"
                      name="sursalaire"
                      value={formData.sursalaire}
                      onChange={(e) => setFormData({ ...formData, sursalaire: e.target.value })}
                      className={`${inputClass} pr-14`}
                      required
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-400">{modalDevise}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between rounded-xl bg-blue-50 px-4 py-3 text-blue-700">
                <span className="text-sm font-medium">Rémunération totale</span>
                <span className="text-lg font-bold tabular-nums">{formTotal ? `${fmt(formTotal)} ${modalDevise}` : '—'}</span>
              </div>

              <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="rounded-xl bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow-sm shadow-blue-600/30 transition hover:bg-blue-700"
                >
                  {editData ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ---------- Confirmation de suppression ---------- */}
      {deleteTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4 backdrop-blur-sm"
          onMouseDown={(e) => e.target === e.currentTarget && setDeleteTarget(null)}
        >
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-red-600">
              <AlertTriangle className="h-7 w-7" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">Supprimer cette grille ?</h3>
            <p className="mt-2 text-sm text-gray-500">
              Êtes-vous sûr de vouloir supprimer la grille{' '}
              <span className="font-semibold text-gray-700">{deleteTarget.Libelle_categorie}</span> ? Cette action est définitive.
            </p>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={() => handleDelete(deleteTarget.idgrille_personnel)}
                className="flex-1 rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GrillesPersonnel;